import { SignInUrlBuilder } from '@tableau/shared-urls/lib/src/SignInUrlBuilder';
import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { AuthenticationType, DomainFamily } from '~src/settings/types/IServerSettingsUnauthenticated';
import { ReactDirectiveProps } from '~src/ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';
import { AutoLoginPage } from './AutoLoginPage';
import { ExternalIdPLogin } from './ExternalIdPLogin';
import { LegacyLoginController } from './LegacyLoginController';
import { SamlLogin } from './SamlLogin';
import { SiteSpecificSamlLogin } from './SiteSpecificSamlLogin';
import { UsernameAndPasswordLogin } from './UsernameAndPasswordLogin';
import React = require('react');
import _ = require('underscore');

export interface RootLoginUiProps extends ReactDirectiveProps {
  signinQueryParams: SignInUrlBuilder.QueryParams;
}

function hasUsernameAndPasswordOverrideByQueryString(signinQueryParams: SignInUrlBuilder.QueryParams): boolean {
  return signinQueryParams.auth?.toUpperCase() as AuthenticationType === AuthenticationType.External;
}

export function RootLoginUi(props: RootLoginUiProps) {

  if (!FeatureFlags.migratedReactLoginPageEnabled) {
    return <LegacyLoginController {...props} />;
  }

  const isOnline = ServerSettingsUnauthenticated.domainFamily() === DomainFamily.External;

  if (isOnline && hasUsernameAndPasswordOverrideByQueryString(props.signinQueryParams)) {
    // On Online, we allow a special querystring argument that will force the UI to do username+password.
    // Server Admins use this to log in to the pod directly using TableauID credentials, instead of going through
    // a whole SAML flow against an IdP and picking a site.
    return <UsernameAndPasswordLogin {...props} />;
  }

  const serverAuthType = AuthenticationTypeResolutionService.authenticationType();

  if (!isOnline && ServerSettingsUnauthenticated.isSiteSamlEnabled()) {
    // This UI Site-SAML login flow only applies to OnPrem.
    // Tableau Online will fall back to Server Auth Type (SAML) even though they have Site-SAML enabled.
    // The SAML flow will kick off and redirect to their unified login page to start the flow from there.
    return <SiteSpecificSamlLogin {...props} serverAuthType={serverAuthType}/>;
  }

  switch (serverAuthType) {
    case AuthenticationType.OpenId:
      return <ExternalIdPLogin {...props} />;

    case AuthenticationType.Saml:
      return <SamlLogin {...props} />;

    case AuthenticationType.Kerberos:
    case AuthenticationType.Sspi:
    case AuthenticationType.ClientCertificate:
    case AuthenticationType.ClientCertificateWithFallback:
      return <AutoLoginPage {...props} serverAuthType={serverAuthType} />;

    case AuthenticationType.AD:
    case AuthenticationType.Local:
    case AuthenticationType.External:
      return <UsernameAndPasswordLogin {...props} />;

    default: assertUnreachable(serverAuthType);
  }
  throw new Error('Invalid auth type');
}
